import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Navigate } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { getDefaultWorkspaceRoute } from 'helpers/workspace';
import AppContext from 'context/Context';

const BLOCKED_STATUSES = ['suspended', 'disabled', 'archived'];

const ProtectedRoute = ({ children, isAuthenticated, requiredPermission, requiredAnyPermissions, allowedModes, requireSuperAdmin, requiredPlanFeature, allowBillingGate }) => {
  const { hasPermission, hasAnyPermission, hasPlanFeature, getCurrentWorkspaceMode, isSuperAdmin } = useAuth();
  const { userData } = useContext(AppContext);
  const currentMode = getCurrentWorkspaceMode();

  if (!isAuthenticated) {
    return <Navigate to='/login' replace />;
  }

  // Check trial/account status from stored context
  if (!isSuperAdmin()) {
    const account = userData?.company?.account;
    if (account) {
      const isTrialExpired = account.status === 'trial' && account.trial_end && new Date(account.trial_end) < new Date();
      const billingRequired = Boolean(account?.billing?.billing_required) || isTrialExpired;
      if (billingRequired && !allowBillingGate) {
        return <Navigate to='/subscription/checkout' replace />;
      }
      if (BLOCKED_STATUSES.includes(account.status) || account.is_service_enabled === false) {
        return <Navigate to='/trial-expired' replace />;
      }
    }
  }

  if (!isSuperAdmin() && Array.isArray(allowedModes) && allowedModes.length && !allowedModes.includes(currentMode)) {
    return <Navigate to={getDefaultWorkspaceRoute(currentMode)} replace />;
  }

  if (requireSuperAdmin && !isSuperAdmin()) {
    return <Navigate to={getDefaultWorkspaceRoute(currentMode)} replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to={getDefaultWorkspaceRoute(currentMode)} replace />;
  }

  if (requiredAnyPermissions && !hasAnyPermission(requiredAnyPermissions)) {
    return <Navigate to={getDefaultWorkspaceRoute(currentMode)} replace />;
  }

  if (requiredPlanFeature && !hasPlanFeature(requiredPlanFeature)) {
    return <Navigate to='/subscription' replace />;
  }

  return isAuthenticated 
    ? children
    : <Navigate to='/login' replace />;
};
ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  isAuthenticated: PropTypes.bool,
  requiredPermission: PropTypes.string,
  requiredAnyPermissions: PropTypes.arrayOf(PropTypes.string),
  allowedModes: PropTypes.arrayOf(PropTypes.string),
  requireSuperAdmin: PropTypes.bool,
  requiredPlanFeature: PropTypes.string,
  allowBillingGate: PropTypes.bool,
};

export default ProtectedRoute;
